import { format, trimSiteDomain, truncateText } from '../../../../../utilities/helpers';
import { EDITABLE_STAGE_FLOW, JOB_STAGE_FILTERS, JOB_STAGES, MODAL_BORDER, MODAL_BORDER_DARK } from './constants';

const normalizeStage = (stage = '') => String(stage).toLowerCase();

const PROCESSING_STAGES = new Set([
	JOB_STAGES.FETCHING_ARTICLE,
	JOB_STAGES.GENERATING_TEMPLATE_JSON,
	JOB_STAGES.RENDERING_VIDEO_START,
	JOB_STAGES.UPLOADING,
	JOB_STAGES.TRANSCODING,
	JOB_STAGES.POLLING,
	JOB_STAGES.CREATING_PLAYLIST,
	JOB_STAGES.SYNCING_PLAYLIST
]);

export const isCompletedStage = (stage = '') => normalizeStage(stage) === JOB_STAGES.COMPLETED;

export const isFailedStage = (stage = '') => normalizeStage(stage) === JOB_STAGES.FAILED;

export const isCancelledStage = (stage = '') => normalizeStage(stage) === JOB_STAGES.CANCELLED;

export const isQueuedStage = (stage = '') => normalizeStage(stage) === JOB_STAGES.QUEUED;

export const isTranscodeStage = (stage = '') => normalizeStage(stage) === JOB_STAGES.TRANSCODING;

export const isProcessingStage = (stage = '') => PROCESSING_STAGES.has(normalizeStage(stage));

export const isTerminalStage = (stage = '') => {
	return isCompletedStage(stage) || isFailedStage(stage) || isCancelledStage(stage);
};

export const isCancellingStage = (stage = '') => normalizeStage(stage) === JOB_STAGES.CANCELLING;

export const isCancellingJob = (job) =>
	!isTerminalStage(job?.stage) && (Boolean(job?.cancelRequested) || isCancellingStage(job?.stage));

export const getJobDisplayStage = (job) => isCancellingJob(job) ? JOB_STAGES.CANCELLING : job?.stage;

export const getStageLabel = (stage) => {
	if (!stage) {
		return '-';
	}

	return String(stage).replace(/_/g, ' ').toUpperCase();
};

export const matchesJobStageFilter = (stage = '', filter = JOB_STAGE_FILTERS.ALL) => {
	if (!filter || filter === JOB_STAGE_FILTERS.ALL) {
		return true;
	}

	if (filter === JOB_STAGE_FILTERS.QUEUED) {
		return isQueuedStage(stage);
	}

	if (filter === JOB_STAGE_FILTERS.PROCESSING) {
		return isProcessingStage(stage);
	}

	if (filter === JOB_STAGE_FILTERS.COMPLETED) {
		return isCompletedStage(stage);
	}

	if (filter === JOB_STAGE_FILTERS.FAILED) {
		return isFailedStage(stage);
	}

	if (filter === JOB_STAGE_FILTERS.CANCELLED) {
		return isCancelledStage(stage);
	}

	return true;
};

export const getErrorMessage = (error) => {
	return error?.response?.data?.data?.message || error?.response?.data?.message || error.message;
};

export const getStageChipColor = (stage = '') => {
	if (isCompletedStage(stage)) {
		return 'success';
	}

	if (isFailedStage(stage)) {
		return 'error';
	}

	if (isCancelledStage(stage) || isCancellingStage(stage)) {
		return 'warning';
	}

	if (isQueuedStage(stage) || isProcessingStage(stage)) {
		return 'info';
	}

	return 'info';
};

export const renderValue = (value) => {
	if (value === null || typeof value === 'undefined' || value === '') {
		return '-';
	}

	if (typeof value === 'boolean') {
		return value ? 'Yes' : 'No';
	}

	return String(value);
};

export const formatDateTime = (value) => {
	if (!value) {
		return '-';
	}

	const parsedDate = new Date(value);

	if (Number.isNaN(parsedDate.getTime())) {
		return renderValue(value);
	}

	return format(parsedDate, 'MMM dd, yyyy hh:mm a');
};

export const getDisplayUrl = (url) => {
	if (!url) {
		return '-';
	}

	return truncateText(trimSiteDomain(url), 50);
};

export const canRetryJob = (job) => isFailedStage(job?.stage) || isCancelledStage(job?.stage);

export const canShowCancelAction = (job) => !isTerminalStage(job?.stage);

export const isCancelBlocked = (job) => isTranscodeStage(job?.stage) || isCancellingJob(job);

export const getProgressValue = (progress) => {
	const value = Number(progress);

	if (!Number.isFinite(value)) {
		return null;
	}

	const percentage = value <= 1 ? value * 100 : value;

	return Math.round(Math.max(0, Math.min(100, percentage)));
};

export const buildMetadataState = (record = {}) => ({
	debug: Boolean(record.debug),
	skipDelivery: Boolean(record.skipDelivery),
	muted: Boolean(record.muted),
	lastCompletedStage: record.lastCompletedStage ?? ''
});

export const getResumeStageOptions = (lastCompletedStage) => {
	if (!lastCompletedStage) {
		return [];
	}

	const stageIndex = EDITABLE_STAGE_FLOW.indexOf(lastCompletedStage);

	if (stageIndex === -1) {
		return [lastCompletedStage];
	}

	const options = [lastCompletedStage];

	if (stageIndex > 0) {
		options.unshift(EDITABLE_STAGE_FLOW[stageIndex - 1]);
	}

	if (stageIndex < EDITABLE_STAGE_FLOW.length - 1) {
		options.push(EDITABLE_STAGE_FLOW[stageIndex + 1]);
	}

	return [...new Set(options)];
};

export const getModalBorder = (theme) =>
	theme.palette.mode === 'dark' ? MODAL_BORDER_DARK : MODAL_BORDER;

export const getTranscodeUrl = (record = {}) => {
	return (
		record.transcodedUrl ||
		null
	);
};

// Only persisted form fields participate in feed configuration comparisons.
export const getComparableFeedConfig = (config = {}) => ({
	feedUrl: config.feedUrl?.trim() ?? null,
	dailyLimit: config.dailyLimit == null ? null : Number(config.dailyLimit),
	pollIntervalMinutes: config.pollIntervalMinutes != null
		? Number(config.pollIntervalMinutes)
		: config.pollIntervalMs != null ? Number(config.pollIntervalMs) / 60000 : null,
	compositionId: config.compositionId ?? null,
	language: config.language ?? null,
	active: config.active == null ? null : config.active === true || config.active === 'true'
});

export const hasFeedConfigChanges = (serviceConfig, savedConfig) => {
	if (!serviceConfig || !savedConfig) return Boolean(serviceConfig) !== Boolean(savedConfig);

	const serviceFields = getComparableFeedConfig(serviceConfig);
	const savedFields = getComparableFeedConfig(savedConfig);
	return Object.keys(serviceFields).some((field) => serviceFields[field] !== savedFields[field]);
};
